import type { CSSProperties } from 'react';
import type { HrRecruitmentPipelineStage } from '@/types/hr';

/** Default workflow stages when HR has not configured pipeline columns yet. */
export const RECRUITMENT_STAGE_ORDER = [
  'APPLIED',
  'SCREENING',
  'INTERVIEW_SCHEDULED',
  'INTERVIEWED',
  'SELECTED',
  'OFFER_SENT',
  'OFFER_ACCEPTED',
  'OFFER_DECLINED',
  'PREBOARDING',
  'ONBOARDING',
  'HIRED',
  'REJECTED',
] as const;

export const STAGE_LABELS: Record<string, string> = {
  APPLIED: 'Applied',
  SCREENING: 'Screening',
  INTERVIEW_SCHEDULED: 'Interview scheduled',
  INTERVIEWED: 'Interviewed',
  SELECTED: 'Selected',
  OFFER_SENT: 'Offer sent',
  OFFER_ACCEPTED: 'Offer accepted',
  OFFER_DECLINED: 'Offer declined',
  PREBOARDING: 'Preboarding',
  ONBOARDING: 'Onboarding',
  HIRED: 'Hired',
  REJECTED: 'Rejected',
  SEARCH: 'Search results',
};

export function stageLabel(code: string, definitions?: HrRecruitmentPipelineStage[]): string {
  const configured = definitions?.find((s) => s.code === code);
  if (configured) return configured.name;
  return STAGE_LABELS[code] ?? code.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

export function resolvePipelineStages(definitions?: HrRecruitmentPipelineStage[]): HrRecruitmentPipelineStage[] {
  if (definitions?.length) {
    return [...definitions].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  }
  return RECRUITMENT_STAGE_ORDER.map((code, index) => ({
    id: index,
    companyId: 0,
    code,
    name: STAGE_LABELS[code] ?? code,
    color: '#64748b',
    sortOrder: (index + 1) * 10,
    active: true,
  }));
}

export function stageColor(code: string, definitions?: HrRecruitmentPipelineStage[]): string {
  return definitions?.find((s) => s.code === code)?.color ?? '#64748b';
}

export function stageColumnStyles(color: string): { column: CSSProperties; header: CSSProperties } {
  const hex = color.startsWith('#') ? color : `#${color}`;
  return {
    column: { borderColor: hex, backgroundColor: `${hex}14` },
    header: { borderBottomColor: `${hex}55`, backgroundColor: `${hex}22` },
  };
}

export const MANPOWER_REQUEST_TYPES = [
  { value: 'NEW_POSITION', label: 'New position' },
  { value: 'REPLACEMENT', label: 'Replacement' },
  { value: 'BACKFILL', label: 'Backfill' },
  { value: 'EXPANSION', label: 'Expansion' },
  { value: 'HEADCOUNT', label: 'Headcount planning' },
  { value: 'BUDGET_REQUEST', label: 'Budget request' },
];

export const CANDIDATE_SOURCES = [
  { value: 'INTERNAL', label: 'Internal recruitment' },
  { value: 'REFERRAL', label: 'Employee referral' },
  { value: 'JOB_BOARD', label: 'Job board' },
  { value: 'LINKEDIN', label: 'LinkedIn' },
  { value: 'INDEED', label: 'Indeed' },
  { value: 'NAUKRI', label: 'Naukri' },
  { value: 'AGENCY', label: 'Consultancy / agency' },
  { value: 'CAMPUS', label: 'Campus recruitment' },
  { value: 'WALK_IN', label: 'Walk-in' },
  { value: 'CONTRACT', label: 'Contract hiring' },
  { value: 'FREELANCER', label: 'Freelancer' },
  { value: 'BULK', label: 'Bulk hiring' },
  { value: 'DIRECT', label: 'Direct application' },
  { value: 'OTHER', label: 'Other' },
];

export const POSTING_CHANNELS = [
  { value: 'CAREER_PORTAL', label: 'Career portal' },
  { value: 'INTERNAL', label: 'Internal jobs' },
  { value: 'LINKEDIN', label: 'LinkedIn' },
  { value: 'INDEED', label: 'Indeed' },
  { value: 'NAUKRI', label: 'Naukri' },
  { value: 'MONSTER', label: 'Monster' },
  { value: 'REFERRAL', label: 'Referral portal' },
  { value: 'AGENCY', label: 'Recruitment agencies' },
  { value: 'CAMPUS', label: 'Campus drives' },
];

export const ASSESSMENT_TYPES = [
  { value: 'TECHNICAL', label: 'Technical test' },
  { value: 'CODING', label: 'Coding test' },
  { value: 'APTITUDE', label: 'Aptitude test' },
  { value: 'LANGUAGE', label: 'Language test' },
  { value: 'CUSTOM', label: 'Custom assessment' },
];

export const INTERVIEW_ROUNDS = [
  { value: 'HR_ROUND', label: 'HR round' },
  { value: 'TECHNICAL_ROUND', label: 'Technical round' },
  { value: 'MANAGER_ROUND', label: 'Manager round' },
  { value: 'PANEL_ROUND', label: 'Panel round' },
  { value: 'FINAL_ROUND', label: 'Final round' },
];

export const PRIORITY_OPTIONS = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'URGENT', label: 'Urgent' },
];

type StatusVariant = 'default' | 'success' | 'warning' | 'error' | 'info';

export function recruitmentStatusVariant(status: string): StatusVariant {
  const code = status.toUpperCase();
  if (['APPROVED', 'OPEN', 'COMPLETED', 'HIRED', 'ACCEPTED'].some((s) => code.includes(s))) return 'success';
  if (['PENDING', 'DRAFT', 'SUBMITTED', 'SCHEDULED'].some((s) => code.includes(s))) return 'warning';
  if (['REJECTED', 'DECLINED', 'CLOSED', 'CANCELLED'].some((s) => code.includes(s))) return 'error';
  if (['PENDING_APPROVAL', 'OFFER_SENT'].some((s) => code.includes(s))) return 'info';
  return 'default';
}
